import { INK, ink, rr, shade } from './cartoon';
import type { MolePose } from './mole';

/**
 * The creatures a mole can be. A race is only a different drawing (ears, snout, markings, quills,
 * tail): every race stands in the mole's frame, so the hitbox, stats and simulation never see it.
 * Each routine works in the mole's local space (feet at the origin, facing +x, up is -y) and is
 * called from drawMole at the same point in the draw order as the part it replaces, so the
 * creature animates with every pose for free. The mole itself is still drawn inline in mole.ts.
 */

type Ctx = CanvasRenderingContext2D;
type Pt = readonly [number, number];
const TAU = Math.PI * 2;

export const RACE_MOLE = 'race.mole';
/** Every race there is art for, the mole first. A race id we do not know draws as the mole. */
export const DRAWN_RACES = ['race.mole', 'race.rat', 'race.rabbit', 'race.hedgehog', 'race.badger'] as const;
export type RaceId = (typeof DRAWN_RACES)[number];

export function drawnRace(id: string | undefined): RaceId {
  return (DRAWN_RACES as readonly string[]).includes(id as string) ? (id as RaceId) : RACE_MOLE;
}

/** The mole's measurements for this body type, in the local frame. */
export interface Frame {
  w: number;
  h: number;
  legs: number;
  torsoTop: number;
  headR: number;
  headCy: number;
  round: number;
}

/** What a head outfit does to the creature underneath. */
export type HatKind = 'none' | 'cap' | 'band' | 'hood' | 'helmet';
const HAT_KINDS: Record<string, HatKind> = {
  'outfit.hardhat': 'cap',
  'outfit.tophat': 'cap',
  'outfit.crown': 'cap',
  'outfit.laurels': 'cap',
  'outfit.bandana': 'band',
  'outfit.goggles': 'band',
  'outfit.headhunter': 'band',
  'outfit.hoodie': 'hood',
  'outfit.astronaut': 'helmet',
};
/** Sits on the head ('cap'), wraps it ('band'), covers it ('hood'), shuts it in a bubble ('helmet'), or leaves it alone ('none'). */
export function hatKind(outfit: string): HatKind {
  return HAT_KINDS[outfit] ?? 'none';
}

/** The moment being drawn: what the loose parts (ears, tail, quills) react to. */
export interface Stance {
  pose: MolePose;
  walk: number;
  spin: number;
  flash: boolean;
  hat: HatKind;
}

export interface Palette {
  /** Torso, and the head unless the race paints its own. */
  fur: string;
  belly: string;
  /** Arms and legs, the paws on the ends of the arms, the feet. */
  limb: string;
  hand: string;
  foot: string;
  /** Muzzle or face patch. */
  face: string;
  /** Inner ears, nose, tail. */
  pink: string;
}

export function molePalette(fur: string): Palette {
  return { fur, belly: shade(fur, 42), limb: fur, hand: fur, foot: shade(fur, -40), face: shade(fur, 42), pink: '#e9a3ab' };
}

export function racePalette(race: RaceId, furColor?: string): Palette {
  switch (race) {
    case 'race.rat': {
      const fur = furColor ?? '#9c98a4';
      return { fur, belly: shade(fur, 40), limb: fur, hand: '#f0a8b8', foot: '#f0a8b8', face: shade(fur, 30), pink: '#f0a0b2' };
    }
    case 'race.hedgehog': {
      // The fur is the quills; the face, belly and limbs are cream.
      const fur = furColor ?? '#7d5433';
      return { fur, belly: '#f7e8c8', limb: '#ecd0a0', hand: '#ecd0a0', foot: '#c49c68', face: '#f3dcb2', pink: '#e9a3ab' };
    }
    case 'race.badger': {
      const fur = furColor ?? '#8b919d';
      return { fur, belly: shade(fur, 36), limb: shade(fur, -52), hand: shade(fur, -52), foot: '#2e2e35', face: '#f4f2ea', pink: '#e9a3ab' };
    }
    case 'race.rabbit': {
      const fur = furColor ?? '#f2ece2';
      return { fur, belly: '#ffffff', limb: fur, hand: fur, foot: shade(fur, -22), face: '#ffffff', pink: '#f4a6b8' };
    }
    default:
      return molePalette(furColor ?? '#a97a50');
  }
}

/** Where the head outfits that care about the face go, in head radii from the head centre. */
export interface HeadFit {
  /** Astronaut bubble: centre x, radius. */
  helmet: readonly [number, number];
  /** Goggle lens centre. */
  lens: readonly [number, number];
  /** Top of the goggle strap, relative to the head centre. */
  strapY: number;
}

/** The mole's fit, which the head outfits were drawn for. */
export const MOLE_FIT: HeadFit = { helmet: [0.15, 1.38], lens: [0.3, -0.2], strapY: -0.45 };

const FITS: Record<RaceId, HeadFit> = {
  'race.mole': MOLE_FIT,
  'race.rat': { helmet: [0.34, 1.66], lens: [0.3, -0.26], strapY: -0.5 },
  'race.rabbit': { helmet: [0.15, 1.38], lens: [0.36, -0.22], strapY: -0.46 },
  'race.hedgehog': { helmet: [0.2, 1.44], lens: [0.4, -0.12], strapY: -0.38 },
  'race.badger': { helmet: [0.22, 1.46], lens: [0.36, -0.18], strapY: -0.44 },
};

export function headFit(race: RaceId): HeadFit {
  return FITS[race];
}

/** How far past the usual mole box a creature reaches, so the hit flash covers all of it: [up in head radii, back in torso widths]. */
export function flashReach(race: RaceId): readonly [number, number] {
  switch (race) {
    case 'race.rat':
      return [0.3, 0.9];
    case 'race.rabbit':
      return [1.1, 0.1];
    case 'race.hedgehog':
      return [0.3, 0.2];
    case 'race.badger':
      return [0.1, 0.25];
    default:
      return [0, 0];
  }
}

// ---------------------------------------------------------------------------------------------
// Shared drawing helpers
// ---------------------------------------------------------------------------------------------

function disc(ctx: Ctx, x: number, y: number, rad: number): void {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.1, rad), 0, TAU);
}

function cubic(p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): [number, number, number, number] {
  const u = 1 - t;
  const x = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
  const y = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
  const dx = 3 * u * u * (p1[0] - p0[0]) + 6 * u * t * (p2[0] - p1[0]) + 3 * t * t * (p3[0] - p2[0]);
  const dy = 3 * u * u * (p1[1] - p0[1]) + 6 * u * t * (p2[1] - p1[1]) + 3 * t * t * (p3[1] - p2[1]);
  return [x, y, dx, dy];
}

/** Path of a tapered limb along a cubic spine, `width(t)` wide at t in [t0, t1]. No fill. */
function ribbon(ctx: Ctx, p0: Pt, p1: Pt, p2: Pt, p3: Pt, width: (t: number) => number, t0 = 0, t1 = 1, steps = 14): void {
  const left: Pt[] = [];
  const right: Pt[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = t0 + ((t1 - t0) * i) / steps;
    const [x, y, dx, dy] = cubic(p0, p1, p2, p3, t);
    const len = Math.hypot(dx, dy) || 1;
    const hw = Math.max(0, width(t)) / 2;
    left.push([x - (dy / len) * hw, y + (dx / len) * hw]);
    right.push([x + (dy / len) * hw, y - (dx / len) * hw]);
  }
  ctx.beginPath();
  ctx.moveTo(left[0]![0], left[0]![1]);
  for (let i = 1; i < left.length; i++) ctx.lineTo(left[i]![0], left[i]![1]);
  for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i]![0], right[i]![1]);
  ctx.closePath();
}

/** A long ear's width along its length: thick base, widest near the first third, a round tip. */
function earProfile(t: number): number {
  if (t < 0.34) return 0.66 + 0.34 * (t / 0.34);
  const u = (t - 0.34) / 0.66;
  return Math.sqrt(Math.max(0, 1 - u * u));
}

/** A leaf: blunt at the base, round at the tip. */
function leafProfile(t: number): number {
  return Math.sin(Math.PI * (0.16 + 0.84 * t));
}

/** The eye every race shares: white with a pupil that looks ahead, an X when dead, wide when tumbling. */
function eye(ctx: Ctx, st: Stance, x: number, y: number, rx: number, ry: number, r: number): void {
  if (st.pose === 'dead') {
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x - r * 0.24, y - r * 0.22);
    ctx.lineTo(x + r * 0.24, y + r * 0.22);
    ctx.moveTo(x - r * 0.24, y + r * 0.22);
    ctx.lineTo(x + r * 0.24, y - r * 0.22);
    ctx.stroke();
    return;
  }
  const k = rx / 5.4;
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
  ink(ctx, '#ffffff', 1.6);
  ctx.fillStyle = INK;
  ctx.beginPath();
  if (st.pose === 'ragdoll') ctx.arc(x + 1.5 * k, y + 1.5 * k, 3.4 * k, 0, TAU);
  else ctx.arc(x + 1.6 * k, y + 0.6 * k, 2.7 * k, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(x + 2.4 * k, y - 0.6 * k, Math.max(0.8, k), 0, TAU);
  ctx.fill();
}

/** A smile, or a startled open mouth when hit or tumbling. (x, y) is where the mole's smile is centred. */
function mouth(ctx: Ctx, st: Stance, x: number, y: number, r: number): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  if (st.flash || st.pose === 'ragdoll') {
    ctx.ellipse(x, y + r * 0.12, 3, 3.6, 0, 0, TAU);
    ctx.fillStyle = '#6b2a2a';
    ctx.fill();
  } else {
    ctx.arc(x, y, 4.5, 0.35, Math.PI - 0.5);
  }
  ctx.stroke();
}

function whiskers(ctx: Ctx, lines: readonly (readonly [number, number, number, number])[], cy: number, r: number): void {
  ctx.strokeStyle = 'rgba(42,26,18,0.75)';
  ctx.lineWidth = 1.1;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const [x0, y0, x1, y1] of lines) {
    ctx.moveTo(x0 * r, cy + y0 * r);
    ctx.lineTo(x1 * r, cy + y1 * r);
  }
  ctx.stroke();
}

/** A round nose with the little white glint every nose in this game has. */
function nose(ctx: Ctx, x: number, y: number, rx: number, ry: number, fill: string, rot = 0): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, rot, 0, TAU);
  ink(ctx, fill, 1.5);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.beginPath();
  ctx.arc(x - rx * 0.25, y - ry * 0.35, Math.max(0.7, rx * 0.26), 0, TAU);
  ctx.fill();
}

interface Edge {
  x: number;
  y: number;
  nx: number;
  ny: number;
}

/** Where a ray from the centre of a rounded box meets its edge, and the outward normal there. */
function boxEdge(hw: number, hh: number, rad: number, ang: number): Edge {
  const dx = Math.cos(ang);
  const dy = Math.sin(ang);
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const t = Math.min(ax > 1e-6 ? hw / ax : Infinity, ay > 1e-6 ? hh / ay : Infinity);
  let x = dx * t;
  let y = dy * t;
  let nx = Math.abs(x) >= hw - 1e-6 ? Math.sign(x) : 0;
  let ny = Math.abs(y) >= hh - 1e-6 ? Math.sign(y) : 0;
  const kx = hw - rad;
  const ky = hh - rad;
  if (rad > 0 && Math.abs(x) > kx && Math.abs(y) > ky) {
    const cx = Math.sign(x) * kx;
    const cy = Math.sign(y) * ky;
    const b = dx * cx + dy * cy;
    const disc2 = b * b - (cx * cx + cy * cy - rad * rad);
    if (disc2 >= 0) {
      const tt = b + Math.sqrt(disc2);
      x = dx * tt;
      y = dy * tt;
      nx = (x - cx) / rad;
      ny = (y - cy) / rad;
    }
  }
  return { x, y, nx, ny };
}

/** n+1 points spaced evenly along a rounded box's edge between two ray angles. */
function edgeSpan(hw: number, hh: number, rad: number, a0: number, a1: number, n: number): Edge[] {
  const M = 40;
  const pts: Edge[] = [];
  const cum: number[] = [0];
  for (let i = 0; i <= M; i++) {
    pts.push(boxEdge(hw, hh, rad, a0 + ((a1 - a0) * i) / M));
    if (i > 0) cum.push(cum[i - 1]! + Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y));
  }
  const total = cum[M]!;
  const out: Edge[] = [];
  let j = 0;
  for (let k = 0; k <= n; k++) {
    const target = (total * k) / n;
    while (j < M - 1 && cum[j + 1]! < target) j++;
    const seg = cum[j + 1]! - cum[j]! || 1;
    const u = Math.min(1, Math.max(0, (target - cum[j]!) / seg));
    const p = pts[j]!;
    const q = pts[j + 1]!;
    const nx = p.nx + (q.nx - p.nx) * u;
    const ny = p.ny + (q.ny - p.ny) * u;
    const nl = Math.hypot(nx, ny) || 1;
    out.push({ x: p.x + (q.x - p.x) * u, y: p.y + (q.y - p.y) * u, nx: nx / nl, ny: ny / nl });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Behind the body: tails
// ---------------------------------------------------------------------------------------------

/** Side-to-side swing of the loose parts: in step with the legs when walking (`rate` 2 bobs twice per stride), flapping while tumbling. */
function swing(st: Stance, rate = 1): number {
  if (st.pose === 'walk') return Math.sin(st.walk * rate);
  if (st.pose === 'ragdoll') return Math.sin(st.spin * 3);
  return 0;
}

/** How much a pose throws the tail up: 0 resting, 1 flung. */
function tailLift(pose: MolePose): number {
  switch (pose) {
    case 'ragdoll':
      return 1;
    case 'kick':
      return 0.6;
    case 'throw':
    case 'gun':
      return 0.35;
    case 'dead':
      return -0.25;
    default:
      return 0;
  }
}

function ratTail(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const { w, h, legs, torsoTop: top } = f;
  const sway = swing(st);
  const lift = tailLift(st.pose);
  const x0 = -w / 2 + 3;
  const y0 = top + h * 0.8;
  if (st.pose === 'dead') {
    // Lying on its back, "behind" points into the ground: the tail trails along it instead. The
    // ground meets this frame at x = -6, where drawMole sinks a dead creature's back.
    ribbon(ctx, [x0, y0], [x0 + 3, y0 + 4], [-6, y0 + 12], [-6, y0 + 26], (t) => 3.8 - 2.4 * t);
    ink(ctx, pal.pink, 1.7);
    return;
  }
  const reach = Math.min(w, 28);
  ribbon(
    ctx,
    [x0, y0],
    [x0 - reach * 0.5, y0 + h * 0.04 - lift * 6],
    [x0 - reach * 0.9, -3 - lift * 12 + sway * 2],
    [x0 - reach * 1.3, -legs * 0.5 - lift * 20 + sway * 4],
    (t) => 3.8 - 2.4 * t,
  );
  ink(ctx, pal.pink, 1.7);
}

function pomTail(ctx: Ctx, f: Frame, st: Stance): void {
  const { w, h, torsoTop: top } = f;
  const R = w * 0.27 + 1;
  const cx = -w / 2 - R * 0.3;
  const cy = top + h * 0.72 - swing(st, 2) * 0.8 - tailLift(st.pose) * 3;
  const n = 8;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a0 = (i / n) * TAU;
    const a1 = ((i + 1) / n) * TAU;
    const am = (a0 + a1) / 2;
    if (i === 0) ctx.moveTo(cx + Math.cos(a0) * R, cy + Math.sin(a0) * R);
    ctx.quadraticCurveTo(cx + Math.cos(am) * R * 1.34, cy + Math.sin(am) * R * 1.34, cx + Math.cos(a1) * R, cy + Math.sin(a1) * R);
  }
  ctx.closePath();
  ink(ctx, '#ffffff', 2);
  // A soft crescent so the puff reads as round, not flat.
  ctx.strokeStyle = '#d6dce8';
  ctx.lineWidth = 1.8;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx + R * 0.1, cy + R * 0.12, R * 0.62, 0.3, 1.9);
  ctx.stroke();
}

function badgerTail(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const { w, h, torsoTop: top } = f;
  const sway = swing(st);
  const lift = tailLift(st.pose);
  const x0 = -w / 2 + 3;
  const y0 = top + h * 0.7;
  const p0: Pt = [x0, y0];
  const p1: Pt = [x0 - 4, y0 - 1 - lift * 2];
  const p2: Pt = [x0 - 9, y0 + 1 - lift * 5];
  const p3: Pt = [x0 - 14, y0 + 4 - lift * 9 + sway * 1.5];
  const wid = (t: number) => 8.5 * leafProfile(t);
  ribbon(ctx, p0, p1, p2, p3, wid);
  ink(ctx, pal.fur, 2);
  // A pale tip.
  ribbon(ctx, p0, p1, p2, p3, wid, 0.74, 1);
  ctx.fillStyle = '#eeece4';
  ctx.fill();
  ribbon(ctx, p0, p1, p2, p3, wid);
  ctx.lineWidth = 2;
  ctx.strokeStyle = INK;
  ctx.stroke();
}

/** Everything that sits behind the legs and torso. */
export function drawRaceBack(ctx: Ctx, race: RaceId, f: Frame, pal: Palette, st: Stance): void {
  switch (race) {
    case 'race.rat':
      ratTail(ctx, f, pal, st);
      break;
    case 'race.rabbit':
      pomTail(ctx, f, st);
      break;
    case 'race.badger':
      badgerTail(ctx, f, pal, st);
      break;
    default:
      break;
  }
}

// ---------------------------------------------------------------------------------------------
// Torso
// ---------------------------------------------------------------------------------------------

/** The hedgehog's quill coat: a sawtooth mantle over the back, from the neck round to the rump. */
function quillCoat(ctx: Ctx, f: Frame, pal: Palette): void {
  const { w, h, torsoTop: top, round } = f;
  const hw = w / 2;
  const hh = h / 2;
  const rad = Math.min(hw, hh, (Math.min(w, h) / 2) * round);
  const cy = top + hh;
  const n = 6;
  const edge = edgeSpan(hw, hh, rad, -1.2, -4.45, n);
  const len = Math.max(6, Math.min(w, h) * 0.36);
  const dark = shade(pal.fur, -36);

  ctx.beginPath();
  const v0 = edge[0]!;
  ctx.moveTo(v0.x + v0.nx * 0.6, cy + v0.y + v0.ny * 0.6);
  const tips: Pt[] = [];
  const bases: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = edge[i]!;
    const b = edge[i + 1]!;
    let mx = (a.nx + b.nx) / 2;
    let my = (a.ny + b.ny) / 2;
    // Quills sweep back.
    mx -= 0.75;
    const ml = Math.hypot(mx, my) || 1;
    const tx = (a.x + b.x) / 2 + (mx / ml) * len;
    const ty = cy + (a.y + b.y) / 2 + (my / ml) * len;
    tips.push([tx, ty]);
    bases.push([(a.x + b.x) / 2, cy + (a.y + b.y) / 2]);
    ctx.lineTo(tx, ty);
    ctx.lineTo(b.x + b.nx * 0.6, cy + b.y + b.ny * 0.6);
  }
  // The inner edge: a short-toothed line back up through the middle of the torso to the neck.
  const end = edge[n]!;
  const start = edge[0]!;
  const e: Pt = [end.x, cy + end.y];
  const s: Pt = [start.x, cy + start.y];
  const c1: Pt = [e[0] + w * 0.34, e[1] - h * 0.14];
  const c2: Pt = [w * 0.3, top + h * 0.36];
  // Small quill tips overlapping the belly, all pointing forward and down.
  const teeth = 5;
  const tl = 3.6 * Math.min(1, w / 26 + 0.2);
  let px = e[0];
  let py = e[1];
  for (let i = 1; i <= teeth; i++) {
    const [x, y] = i === teeth ? s : cubic(e, c1, c2, s, i / teeth);
    ctx.lineTo((px + x) / 2 + 0.55 * tl, (py + y) / 2 + 0.83 * tl);
    ctx.lineTo(x, y);
    px = x;
    py = y;
  }
  ctx.closePath();
  ink(ctx, pal.fur, 2.2);

  // Quill lines: one darker stroke up the middle of each spike.
  ctx.lineCap = 'round';
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = dark;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const t = tips[i]!;
    const b = bases[i]!;
    ctx.moveTo(b[0] + (t[0] - b[0]) * 0.1, b[1] + (t[1] - b[1]) * 0.1);
    ctx.lineTo(b[0] + (t[0] - b[0]) * 0.78, b[1] + (t[1] - b[1]) * 0.78);
  }
  ctx.stroke();
}

/** Body of every non-mole race (the mole's own torso is drawn inline in mole.ts). The caller adds the team sash after. */
export function drawRaceTorso(ctx: Ctx, race: RaceId, f: Frame, pal: Palette): void {
  const { w, h, torsoTop: top, round } = f;
  rr(ctx, -w / 2, top, w, h, (Math.min(w, h) / 2) * round);
  if (race === 'race.hedgehog') {
    ink(ctx, pal.face);
    ctx.fillStyle = pal.belly;
    ctx.beginPath();
    ctx.ellipse(w * 0.14, top + h * 0.62, w * 0.26, h * 0.28, 0, 0, TAU);
    ctx.fill();
    quillCoat(ctx, f, pal);
    return;
  }
  ink(ctx, pal.fur);
  ctx.fillStyle = pal.belly;
  ctx.beginPath();
  ctx.ellipse(w * 0.06, top + h * 0.6, w * 0.3, h * 0.28, 0, 0, TAU);
  ctx.fill();
}

// ---------------------------------------------------------------------------------------------
// Heads
// ---------------------------------------------------------------------------------------------

/** A pointed snout grown out of the front of the head: where it leaves the circle, its tip, and its edges' control points (head radii). */
interface Snout {
  at: number;
  ab: number;
  tip: Pt;
  top: Pt;
  bot: Pt;
}

const RAT_SNOUT: Snout = { at: -0.62, ab: 1.0, tip: [1.74, 0.22], top: [1.2, -0.4], bot: [1.25, 0.84] };
const HEDGEHOG_SNOUT: Snout = { at: -0.5, ab: 1.0, tip: [1.42, 0.26], top: [1.05, -0.3], bot: [1.05, 0.72] };
const BADGER_SNOUT: Snout = { at: -0.55, ab: 1.0, tip: [1.52, 0.2], top: [1.1, -0.36], bot: [1.08, 0.7] };

/** The head circle and its snout as one outline. */
function facePath(ctx: Ctx, r: number, cy: number, s: Snout): void {
  ctx.beginPath();
  ctx.moveTo(Math.cos(s.at) * r, cy + Math.sin(s.at) * r);
  ctx.quadraticCurveTo(s.top[0] * r, cy + s.top[1] * r, s.tip[0] * r, cy + s.tip[1] * r);
  ctx.quadraticCurveTo(s.bot[0] * r, cy + s.bot[1] * r, Math.cos(s.ab) * r, cy + Math.sin(s.ab) * r);
  ctx.arc(0, cy, r, s.ab, s.at + TAU);
  ctx.closePath();
}

/** Fill the face, paint markings inside it, then ink its outline once, over the markings' edges. */
function face(ctx: Ctx, r: number, cy: number, s: Snout, fill: string, markings?: () => void): void {
  facePath(ctx, r, cy, s);
  ctx.fillStyle = fill;
  ctx.fill();
  if (markings) {
    ctx.save();
    facePath(ctx, r, cy, s);
    ctx.clip();
    markings();
    ctx.restore();
  }
  facePath(ctx, r, cy, s);
  ctx.lineWidth = 3;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  ctx.stroke();
}

function roundEar(ctx: Ctx, x: number, y: number, rad: number, fur: string, inner: string, innerScale = 0.58): void {
  disc(ctx, x, y, rad);
  ink(ctx, fur, 2.4);
  disc(ctx, x + rad * 0.05, y, rad * innerScale);
  ctx.fillStyle = inner;
  ctx.fill();
}

function ratHead(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const r = f.headR;
  const cy = f.headCy;
  const bob = swing(st, 2) * 0.6;
  if (st.hat === 'none' || st.hat === 'helmet' || st.hat === 'band') {
    // A band wraps the head below the crown: the ears stay up above it.
    roundEar(ctx, 0.12 * r, cy - 0.98 * r + bob * 0.5, 0.42 * r, shade(pal.fur, -16), pal.pink);
    roundEar(ctx, -0.42 * r, cy - 0.78 * r + bob, 0.5 * r, pal.fur, pal.pink);
  } else if (st.hat !== 'hood') {
    // Under a hat: slid down the back of the head so they show below the brim.
    roundEar(ctx, -0.86 * r, cy - 0.28 * r, 0.44 * r, pal.fur, pal.pink);
  }
  // A long pointed snout over a lighter jaw.
  face(ctx, r, cy, RAT_SNOUT, pal.fur, () => {
    ctx.fillStyle = pal.face;
    ctx.beginPath();
    ctx.moveTo(2 * r, cy + 0.3 * r);
    ctx.quadraticCurveTo(1.0 * r, cy + 0.4 * r, 0.45 * r, cy + 0.75 * r);
    ctx.lineTo(0.2 * r, cy + 1.3 * r);
    ctx.lineTo(2 * r, cy + 1.3 * r);
    ctx.closePath();
    ctx.fill();
  });
  // The mouth line, gaping when hit or tumbling.
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(1.6 * r, cy + 0.3 * r);
  ctx.quadraticCurveTo(1.0 * r, cy + 0.4 * r, 0.5 * r, cy + 0.72 * r);
  ctx.stroke();
  if (st.flash || st.pose === 'ragdoll') {
    ctx.beginPath();
    ctx.ellipse(0.98 * r, cy + 0.54 * r, 3.2, 3.8, 0.3, 0, TAU);
    ink(ctx, '#6b2a2a', 1.6);
  }
  // Buck teeth hanging from the upper lip.
  rr(ctx, 1.2 * r, cy + 0.3 * r, 2.8, 5.2, 0.8);
  ink(ctx, '#ffffff', 1.2);
  rr(ctx, 1.2 * r + 2.8, cy + 0.28 * r, 2.8, 5.6, 0.8);
  ink(ctx, '#ffffff', 1.2);
  nose(ctx, 1.7 * r, cy + 0.2 * r, 0.2 * r, 0.17 * r, pal.pink, 0.4);
  eye(ctx, st, 0.32 * r, cy - 0.26 * r, 4.8, 5.6, r);
  whiskers(
    ctx,
    [
      [1.1, 0.12, 1.95, -0.2],
      [1.14, 0.22, 2.0, 0.14],
      [1.12, 0.32, 1.92, 0.5],
    ],
    cy,
    r,
  );
}

/** One long ear: `ang` leans it back from straight up, `bend` curls the tip toward the back or down. */
function rabbitEar(ctx: Ctx, bx: number, by: number, len: number, ang: number, bend: number, wid: number, fur: string, inner: string): void {
  const dx = -Math.sin(ang);
  const dy = -Math.cos(ang);
  const nx = -dy;
  const ny = dx;
  const p0: Pt = [bx, by];
  const p1: Pt = [bx + dx * len * 0.34 + nx * bend * len * 0.1, by + dy * len * 0.34 + ny * bend * len * 0.1];
  const p2: Pt = [bx + dx * len * 0.7 + nx * bend * len * 0.3, by + dy * len * 0.7 + ny * bend * len * 0.3];
  const p3: Pt = [bx + dx * len + nx * bend * len * 0.55, by + dy * len + ny * bend * len * 0.55];
  ribbon(ctx, p0, p1, p2, p3, (t) => wid * earProfile(t));
  ink(ctx, fur, 2.4);
  ribbon(ctx, p0, p1, p2, p3, (t) => wid * 0.5 * Math.pow(Math.sin(Math.PI * t), 0.7), 0.12, 0.86);
  ctx.fillStyle = inner;
  ctx.fill();
}

function rabbitHead(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const r = f.headR;
  const cy = f.headCy;
  const L = r * 1.75;
  const wid = r * 0.5;
  const far = shade(pal.fur, -14);
  const bounce = swing(st, 2) * (st.pose === 'ragdoll' ? 0.3 : 0.12);
  if (st.hat === 'hood') {
    // Out of the back of the hood, hanging.
    rabbitEar(ctx, -1.0 * r, cy - 0.3 * r, L * 0.8, 2.5 + bounce, -0.5, wid, far, pal.pink);
    rabbitEar(ctx, -1.08 * r, cy - 0.05 * r, L * 0.86, 2.75 + bounce, -0.5, wid, pal.fur, pal.pink);
  } else if (st.hat === 'helmet') {
    // Folded back and tucked inside the bubble.
    rabbitEar(ctx, -0.2 * r, cy - 0.55 * r, L * 0.5, 1.9 + bounce, -0.7, wid * 0.9, far, pal.pink);
    rabbitEar(ctx, -0.4 * r, cy - 0.5 * r, L * 0.52, 2.1 + bounce, -0.7, wid * 0.9, pal.fur, pal.pink);
  } else if (st.hat !== 'none' || st.pose === 'dead') {
    // A hat is on, or the rabbit is down: the ears fold back and flop behind the head.
    rabbitEar(ctx, -0.3 * r, cy - 0.45 * r, L * 0.82, 2.0 + bounce, -0.9, wid, far, pal.pink);
    rabbitEar(ctx, -0.5 * r, cy - 0.5 * r, L * 0.88, 2.3 + bounce, -0.9, wid, pal.fur, pal.pink);
  } else {
    const back = st.pose === 'throw' || st.pose === 'gun' ? 0.5 : st.pose === 'kick' ? 0.3 : st.pose === 'ragdoll' ? 0.85 : 0;
    rabbitEar(ctx, 0.14 * r, cy - 0.6 * r, L * 0.94, 0.06 + back * 0.9 + bounce, -0.5, wid, far, pal.pink);
    rabbitEar(ctx, -0.38 * r, cy - 0.58 * r, L, 0.3 + back + bounce, -0.5, wid, pal.fur, pal.pink);
  }
  disc(ctx, 0, cy, r);
  ink(ctx, pal.fur);
  // Round muzzle.
  ctx.beginPath();
  ctx.ellipse(0.62 * r, cy + 0.3 * r, 0.5 * r, 0.4 * r, 0, 0, TAU);
  ink(ctx, pal.face, 1.5);
  // Buck teeth.
  rr(ctx, 0.74 * r, cy + 0.56 * r, 3, 5.2, 0.8);
  ink(ctx, '#ffffff', 1.2);
  rr(ctx, 0.74 * r + 3, cy + 0.56 * r, 3, 5.2, 0.8);
  ink(ctx, '#ffffff', 1.2);
  nose(ctx, 1.08 * r, cy + 0.12 * r, 3.6, 2.8, pal.pink);
  eye(ctx, st, 0.38 * r, cy - 0.22 * r, 5.2, 6.4, r);
  mouth(ctx, st, 0.54 * r, cy + 0.34 * r, r);
  whiskers(
    ctx,
    [
      [1.0, 0.3, 1.5, 0.14],
      [1.0, 0.4, 1.52, 0.46],
    ],
    cy,
    r,
  );
}

function hedgehogCap(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const r = f.headR;
  const cy = f.headCy;
  // Under anything worn the quills lie flat; the back ones on the torso stay as they are.
  const flat = st.hat !== 'none';
  const len = r * (flat ? 0.17 : 0.5);
  const n = 7;
  // A sawtooth from the forehead (angle a0) over the crown and down the back of the head (a1).
  const a0 = -0.9;
  const a1 = -Math.PI * 1.22;
  const da = (a0 - a1) / n;
  const px = (a: number, rad: number) => [Math.cos(a) * rad, cy + Math.sin(a) * rad] as const;
  ctx.beginPath();
  const s0 = px(a0, r * 0.96);
  ctx.moveTo(s0[0], s0[1]);
  const tips: (readonly [number, number])[] = [];
  const valleys: (readonly [number, number])[] = [s0];
  for (let i = 0; i < n; i++) {
    const mid = a0 - (i + 0.5) * da - 0.2;
    const tip = px(mid, r + len);
    const v = px(a0 - (i + 1) * da, r * 0.96);
    tips.push(tip);
    valleys.push(v);
    ctx.lineTo(tip[0], tip[1]);
    ctx.lineTo(v[0], v[1]);
  }
  // The hairline: a curve back across the head to the forehead.
  ctx.quadraticCurveTo(-0.2 * r, cy - 0.15 * r, s0[0], s0[1]);
  ctx.closePath();
  ink(ctx, pal.fur, 2.2);
  // Quill lines.
  ctx.strokeStyle = shade(pal.fur, -36);
  ctx.lineWidth = 1.3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const t = tips[i]!;
    const a = valleys[i]!;
    const b = valleys[i + 1]!;
    const bx = (a[0] + b[0]) / 2;
    const by = (a[1] + b[1]) / 2;
    ctx.moveTo(bx + (t[0] - bx) * 0.1, by + (t[1] - by) * 0.1);
    ctx.lineTo(bx + (t[0] - bx) * 0.76, by + (t[1] - by) * 0.76);
  }
  ctx.stroke();
}

function hedgehogHead(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const r = f.headR;
  const cy = f.headCy;
  face(ctx, r, cy, HEDGEHOG_SNOUT, pal.face);
  hedgehogCap(ctx, f, pal, st);
  if (st.hat === 'none' || st.hat === 'helmet') roundEar(ctx, 0.3 * r, cy - 0.62 * r, 0.22 * r, pal.face, pal.pink, 0.5);
  nose(ctx, 1.38 * r, cy + 0.25 * r, 0.2 * r, 0.17 * r, INK);
  eye(ctx, st, 0.42 * r, cy - 0.12 * r, 4.4, 5.1, r);
  mouth(ctx, st, 0.82 * r, cy + 0.36 * r, r);
}

function badgerHead(ctx: Ctx, f: Frame, pal: Palette, st: Stance): void {
  const r = f.headR;
  const cy = f.headCy;
  const black = '#2a2a31';
  const bounce = swing(st, 2) * 0.5;
  // Small round ears, black with white tips.
  const ear = (x: number, y: number, rad: number) => {
    disc(ctx, x, y, rad);
    ink(ctx, black, 2.2);
    ctx.save();
    disc(ctx, x, y, rad);
    ctx.clip();
    ctx.fillStyle = '#f4f2ea';
    ctx.fillRect(x - rad, y - rad, rad * 2, rad * 0.78);
    ctx.restore();
    disc(ctx, x, y, rad);
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = INK;
    ctx.stroke();
  };
  if (st.hat !== 'hood') {
    ear(0.1 * r, cy - 0.98 * r + bounce * 0.5, 0.24 * r);
    ear(-0.46 * r, cy - 0.84 * r + bounce, 0.27 * r);
  }
  // A white face with a grey nape and a black stripe from the nose through the eye.
  face(ctx, r, cy, BADGER_SNOUT, pal.face, () => {
    ctx.fillStyle = pal.fur;
    ctx.beginPath();
    ctx.moveTo(-0.46 * r, cy - 1.1 * r);
    ctx.quadraticCurveTo(-0.78 * r, cy, -0.36 * r, cy + 1.1 * r);
    ctx.lineTo(-1.2 * r, cy + 1.2 * r);
    ctx.lineTo(-1.2 * r, cy - 1.2 * r);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.moveTo(1.9 * r, cy - 0.4 * r);
    ctx.lineTo(0.95 * r, cy - 0.46 * r);
    ctx.quadraticCurveTo(0.6 * r, cy - 0.56 * r, -0.2 * r, cy - 0.62 * r);
    ctx.lineTo(-0.85 * r, cy - 0.5 * r);
    ctx.lineTo(-0.85 * r, cy + 0.45 * r);
    ctx.quadraticCurveTo(0.1 * r, cy + 0.04 * r, 1.1 * r, cy + 0.17 * r);
    ctx.lineTo(1.9 * r, cy + 0.17 * r);
    ctx.closePath();
    ctx.fill();
  });
  nose(ctx, 1.46 * r, cy + 0.17 * r, 0.19 * r, 0.16 * r, black);
  eye(ctx, st, 0.38 * r, cy - 0.2 * r, 3.9, 4.5, r);
  mouth(ctx, st, 0.8 * r, cy + 0.44 * r, r);
}

/** Everything of the head: ears, face, snout, markings, eye, mouth. Head outfits go on top afterwards. */
export function drawRaceHead(ctx: Ctx, race: RaceId, f: Frame, pal: Palette, st: Stance): void {
  switch (race) {
    case 'race.rat':
      ratHead(ctx, f, pal, st);
      break;
    case 'race.rabbit':
      rabbitHead(ctx, f, pal, st);
      break;
    case 'race.hedgehog':
      hedgehogHead(ctx, f, pal, st);
      break;
    case 'race.badger':
      badgerHead(ctx, f, pal, st);
      break;
    default:
      break;
  }
}
